import Eventable from 'utils/eventable';
import type { Player } from '../../../../../types';

interface AdMeta {
    adId: string;
    sequence: number;
    podcount: number;
    tag: string;
    duration: number;
    adschedule: {
        offset: number | string;
    };
}

/**
 * Parse SVP Player events and tailor data to pulse requirements
 *
 * Available events: Play, Pause, Stop
 */
class AdEvents extends Eventable {
    player: Player;
    adData: {
        id?: string;
        adSequenceDuration: number[];
        position: number;
        start: boolean;
    };

    beforeUnload: [keyof WindowEventMap, (e: unknown) => void, boolean];

    constructor(player: Player) {
        super();

        this.onPlaylistItem = this.onPlaylistItem.bind(this);
        this.onAdSlotStart = this.onAdSlotStart.bind(this);
        this.onAdStarted = this.onAdStarted.bind(this);
        this.onAdProgress = this.onAdProgress.bind(this);
        this.onAdSlotComplete = this.onAdSlotComplete.bind(this);
        this.onAdFinished = this.onAdFinished.bind(this);
        // this.onAdPause = this.onAdPause.bind(this);
        this.onAdPlay = this.onAdPlay.bind(this);
        // this.onPauseAdVisible = this.onPauseAdVisible.bind(this);

        this.player = player;
        this.adData = { adSequenceDuration: [], start: false, position: 0 };
        this.beforeUnload = [
            'onpagehide' in window.self ? 'pagehide' : 'beforeunload',
            this.onAdSlotComplete,
            true,
        ];

        this.listenTo(this.player, 'playlistItem', this.onPlaylistItem);
        this.listenTo(this.player, 'adSlotStart', this.onAdSlotStart);
        this.listenTo(this.player, 'adStarted', this.onAdStarted);
        this.listenTo(this.player, 'adProgress', this.onAdProgress);
        this.listenTo(this.player, 'adSlotComplete', this.onAdSlotComplete);
        this.listenTo(this.player, 'adFinished', this.onAdFinished);
        // this.listenTo(this.player, 'adPause', this.onAdPause);
        this.listenTo(this.player, 'adPlay', this.onAdPlay);
        // this.listenTo(
        //     this.player,
        //     'PausePlugin:adVisible',
        //     this.onPauseAdVisible,
        // );

        this.listenTo(this.player, 'playlistItem', () => {
            this.adData.adSequenceDuration = [];
        });

        window.addEventListener(...this.beforeUnload);
    }

    onPlaylistItem() {
        this.listenToOnce(this.player, 'complete', this.onAdSlotComplete);
    }

    onAdSlotStart(slotData: {
        meta: { adId: string };
        position: number | string;
    }) {
        Object.assign(this.adData, {
            id: slotData.meta.adId,
            type: slotData.position,
        });
    }

    onAdProgress(time: number) {
        if (!this.adData.id) {
            return;
        }

        const position = Math.floor(time);

        Object.assign(this.adData, {
            position,
        });
    }

    onAdSlotComplete() {
        this.sendStopEvent();
        window.removeEventListener(...this.beforeUnload);
    }

    onAdFinished() {
        this.sendStopEvent();
    }

    onAdStarted({ meta: adData }: jwplayer.AdStartedParam & { meta?: AdMeta }) {
        Object.assign(this.adData, {
            start: true,
            placementId: undefined,
        });

        // adData is missing from homad events
        if (!adData) {
            return;
        }

        const placementId =
            new URLSearchParams(adData.tag.split('?')[1])?.get('id') ||
            undefined;

        Object.assign(this.adData, {
            id: adData.adId,
            sequence: adData.sequence,
            count: adData.podcount,
            position: 0,
            placementId,
        });

        if (!this.adData.adSequenceDuration.length) {
            this.adData.adSequenceDuration = Array(adData.podcount);
        }

        const adType = adData?.adschedule?.offset;
        // Preroll ads only
        if (adType === 'pre' && adData.duration) {
            this.adData.adSequenceDuration[adData.sequence - 1] =
                adData.duration;
        }
    }

    onAdPlay() {
        this.trigger('AdStart', this.adData);
        // start indicates first play of ad
        this.adData.start = false;
    }

    // onAdPause() {
    //     this.trigger('Pause', this.adData);
    // }
    //
    // onPauseAdVisible(data: { creativeId: string }) {
    //     this.trigger('View', {
    //         id: data.creativeId,
    //         type: 'Pausead',
    //     });
    // }

    sendStopEvent() {
        if (this.adData.id) {
            this.trigger('AdStop', {
                ...this.adData,
                duration: this.adData.position,
            });
            // keep adSequenceDuration for playback tracking purposes
            this.adData = {
                adSequenceDuration: this.adData.adSequenceDuration,
                position: 0,
                start: false,
            };
        }
    }

    destroy() {
        window.removeEventListener(...this.beforeUnload);
        this.stopListening();
    }
}

export default AdEvents;
